import { useState, useEffect, useCallback } from 'react'
import api from '../api/client'
import { Loading } from '../components/ui'
import { SettingsSEO } from '../hooks/useSEO'
import type { NotificationChannel, UserNotificationChannel } from '../types'

interface ChannelListResponse {
  channels: UserNotificationChannel[]
  availableChannels: {
    telegram: boolean
    whatsapp: boolean
  }
}

interface TelegramLinkResponse {
  linkingCode: string
  deepLink: string
  botUsername: string
  expiresAt: string
  instructions: string
}

interface WhatsAppLinkResponse {
  linkingCode: string
  phoneNumber: string | null
  instructions: string
  connectionStatus: string
  qrCodeDataUrl: string | null
  expiresAt: string
}

export default function SettingsPage() {
  const [channels, setChannels] = useState<UserNotificationChannel[]>([])
  const [availableChannels, setAvailableChannels] = useState<{ telegram: boolean; whatsapp: boolean }>({
    telegram: false,
    whatsapp: false,
  })
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [telegramLink, setTelegramLink] = useState<TelegramLinkResponse | null>(null)
  const [whatsappLink, setWhatsappLink] = useState<WhatsAppLinkResponse | null>(null)
  const [isLinkingTelegram, setIsLinkingTelegram] = useState(false)
  const [isLinkingWhatsApp, setIsLinkingWhatsApp] = useState(false)

  const fetchChannels = useCallback(async () => {
    try {
      const data = await api.get<ChannelListResponse>('/channels')
      setChannels(data.channels)
      setAvailableChannels(data.availableChannels)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load channels')
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchChannels()
  }, [fetchChannels])

  const startTelegramLinking = async () => {
    setIsLinkingTelegram(true)
    setError('')
    setSuccess('')
    try {
      const data = await api.post<TelegramLinkResponse>('/channels/telegram/link')
      setTelegramLink(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start Telegram linking')
    } finally {
      setIsLinkingTelegram(false)
    }
  }

  const startWhatsAppLinking = async () => {
    setIsLinkingWhatsApp(true)
    setError('')
    setSuccess('')
    try {
      const data = await api.post<WhatsAppLinkResponse>('/channels/whatsapp/link')
      setWhatsappLink(data)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start WhatsApp linking')
    } finally {
      setIsLinkingWhatsApp(false)
    }
  }

  const toggleChannel = async (channelId: string, enabled: boolean) => {
    setError('')
    setSuccess('')
    try {
      await api.patch(`/channels/${channelId}/toggle`, { enabled })
      setSuccess(`Channel ${enabled ? 'enabled' : 'disabled'} successfully`)
      fetchChannels()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to toggle channel')
    }
  }

  const testChannel = async (channelId: string) => {
    setError('')
    setSuccess('')
    try {
      await api.post<{ message: string }>(`/channels/${channelId}/test`)
      setSuccess('Test notification sent successfully')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send test notification')
    }
  }

  const unlinkChannel = async (channelId: string) => {
    if (!confirm('Are you sure you want to unlink this notification channel?')) {
      return
    }
    setError('')
    setSuccess('')
    try {
      await api.delete(`/channels/${channelId}`)
      setSuccess('Channel unlinked successfully')
      setTelegramLink(null)
      setWhatsappLink(null)
      fetchChannels()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to unlink channel')
    }
  }

  const getChannelLabel = (channel: NotificationChannel): string => {
    switch (channel) {
      case 'TELEGRAM':
        return 'Telegram'
      case 'WHATSAPP':
        return 'WhatsApp'
      default:
        return channel
    }
  }

  const getTelegramChannel = () => channels.find((c) => c.channel === 'TELEGRAM')
  const getWhatsAppChannel = () => channels.find((c) => c.channel === 'WHATSAPP')

  if (isLoading) return <Loading message="Loading settings..." />

  return (
    <div>
      <SettingsSEO />
      <div className="page-header">
        <h1>Settings</h1>
      </div>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <div className="card-static">
        <h2 style={{ marginBottom: '1.5rem' }}>Notification Channels</h2>
        <p className="text-secondary" style={{ marginBottom: '1.5rem' }}>
          Connect external messaging apps to receive notifications about job applications, payments, and more.
        </p>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
          {/* Telegram Section */}
          <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm4.64 6.8c-.15 1.58-.8 5.42-1.13 7.19-.14.75-.42 1-.68 1.03-.58.05-1.02-.38-1.58-.75-.88-.58-1.38-.94-2.23-1.5-.99-.65-.35-1.01.22-1.59.15-.15 2.71-2.48 2.76-2.69a.2.2 0 00-.05-.18c-.06-.05-.14-.03-.21-.02-.09.02-1.49.95-4.22 2.79-.4.27-.76.41-1.08.4-.36-.01-1.04-.2-1.55-.37-.63-.2-1.12-.31-1.08-.66.02-.18.27-.36.74-.55 2.92-1.27 4.86-2.11 5.83-2.51 2.78-1.16 3.35-1.36 3.73-1.36.08 0 .27.02.39.12.1.08.13.19.14.27-.01.06.01.24 0 .38z" />
              </svg>
              <h3 style={{ margin: 0 }}>Telegram</h3>
              {getTelegramChannel()?.verified && (
                <span className="badge badge-green">Connected</span>
              )}
            </div>

            {!availableChannels.telegram ? (
              <p className="text-secondary">Telegram notifications are not configured on this server.</p>
            ) : getTelegramChannel()?.verified ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={getTelegramChannel()?.enabled ?? false}
                      onChange={(e) => toggleChannel(getTelegramChannel()!.id, e.target.checked)}
                    />
                    Receive notifications
                  </label>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => testChannel(getTelegramChannel()!.id)}>
                    Send Test
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => unlinkChannel(getTelegramChannel()!.id)}>
                    Unlink
                  </button>
                </div>
              </div>
            ) : telegramLink ? (
              <div>
                <p style={{ marginBottom: '1rem' }}>{telegramLink.instructions}</p>
                <a
                  href={telegramLink.deepLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn btn-primary"
                >
                  Open Telegram
                </a>
                <p className="text-secondary" style={{ marginTop: '0.75rem', fontSize: '0.875rem' }}>
                  Code expires at: {new Date(telegramLink.expiresAt).toLocaleTimeString()}
                </p>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ marginTop: '0.5rem' }}
                  onClick={() => {
                    setTelegramLink(null)
                    fetchChannels()
                  }}
                >
                  Cancel / Check Status
                </button>
              </div>
            ) : (
              <button
                className="btn btn-primary"
                onClick={startTelegramLinking}
                disabled={isLinkingTelegram}
              >
                {isLinkingTelegram ? 'Connecting...' : 'Connect Telegram'}
              </button>
            )}
          </div>

          {/* WhatsApp Section */}
          <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, padding: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1rem' }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
              <h3 style={{ margin: 0 }}>WhatsApp</h3>
              {getWhatsAppChannel()?.verified && (
                <span className="badge badge-green">Connected</span>
              )}
            </div>

            {!availableChannels.whatsapp ? (
              <p className="text-secondary">WhatsApp notifications are not configured on this server.</p>
            ) : getWhatsAppChannel()?.verified ? (
              <div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={getWhatsAppChannel()?.enabled ?? false}
                      onChange={(e) => toggleChannel(getWhatsAppChannel()!.id, e.target.checked)}
                    />
                    Receive notifications
                  </label>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="btn btn-secondary btn-sm" onClick={() => testChannel(getWhatsAppChannel()!.id)}>
                    Send Test
                  </button>
                  <button className="btn btn-ghost btn-sm" onClick={() => unlinkChannel(getWhatsAppChannel()!.id)}>
                    Unlink
                  </button>
                </div>
              </div>
            ) : whatsappLink ? (
              <div>
                <p style={{ marginBottom: '1rem' }}>{whatsappLink.instructions}</p>
                {whatsappLink.phoneNumber && (
                  <p style={{ marginBottom: '0.5rem' }}>
                    Phone number: <strong>{whatsappLink.phoneNumber}</strong>
                  </p>
                )}
                <p style={{ marginBottom: '0.5rem' }}>
                  Your linking code: <code style={{ padding: '0.25rem 0.5rem', background: 'var(--color-surface)', borderRadius: 4 }}>{whatsappLink.linkingCode}</code>
                </p>
                {whatsappLink.qrCodeDataUrl && (
                  <div style={{ marginTop: '1rem' }}>
                    <p className="text-secondary" style={{ marginBottom: '0.5rem' }}>Or scan QR code:</p>
                    <img src={whatsappLink.qrCodeDataUrl} alt="WhatsApp QR Code" style={{ maxWidth: 200 }} />
                  </div>
                )}
                <p className="text-secondary" style={{ marginTop: '0.75rem', fontSize: '0.875rem' }}>
                  Code expires at: {new Date(whatsappLink.expiresAt).toLocaleTimeString()}
                </p>
                <button
                  className="btn btn-ghost btn-sm"
                  style={{ marginTop: '0.5rem' }}
                  onClick={() => {
                    setWhatsappLink(null)
                    fetchChannels()
                  }}
                >
                  Cancel / Check Status
                </button>
              </div>
            ) : (
              <button
                className="btn btn-primary"
                onClick={startWhatsAppLinking}
                disabled={isLinkingWhatsApp}
              >
                {isLinkingWhatsApp ? 'Connecting...' : 'Connect WhatsApp'}
              </button>
            )}
          </div>
        </div>

        {/* Connected Channels Summary */}
        {channels.filter((c) => c.verified).length > 0 && (
          <div style={{ marginTop: '2rem' }}>
            <h3 style={{ marginBottom: '1rem' }}>Connected Channels</h3>
            <table className="table">
              <thead>
                <tr>
                  <th>Channel</th>
                  <th>Status</th>
                  <th>Connected</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {channels.filter((c) => c.verified).map((channel) => (
                  <tr key={channel.id}>
                    <td>{getChannelLabel(channel.channel)}</td>
                    <td>
                      <span className={`badge ${channel.enabled ? 'badge-green' : 'badge-yellow'}`}>
                        {channel.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td>{new Date(channel.createdAt).toLocaleDateString()}</td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.25rem' }}>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => toggleChannel(channel.id, !channel.enabled)}
                        >
                          {channel.enabled ? 'Disable' : 'Enable'}
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          onClick={() => testChannel(channel.id)}
                        >
                          Test
                        </button>
                        <button
                          className="btn btn-ghost btn-sm"
                          style={{ color: 'var(--color-error)' }}
                          onClick={() => unlinkChannel(channel.id)}
                        >
                          Unlink
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
